import React, { useState, useCallback } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const T = {
  bg:     '#f7f5f0',
  border: '#e5e0d5',
  text:   '#0f0f0f',
  muted:  '#8b7d6b',
  red:    '#c0392b',
};
const SZ   = 'clamp(12px, 1.1vw, 14px)';
const FONT = "'SuisseSemiBold'";

const S = {
  label:    { fontSize: SZ, letterSpacing: '0.06em', color: T.muted, textTransform: 'uppercase', display: 'block', marginBottom: 6 },
  input:    { width: '100%', background: T.bg, border: `1px solid ${T.border}`, color: T.text, fontSize: 14, padding: '11px 12px', outline: 'none', fontFamily: FONT, boxSizing: 'border-box' },
  textarea: { width: '100%', background: T.bg, border: `1px solid ${T.border}`, color: T.text, fontSize: 14, padding: '11px 12px', outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'vertical', minHeight: 72 },
  btn:      { background: T.text, color: '#fff', border: 'none', padding: '12px 24px', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT },
  ghost:    { background: 'transparent', color: T.muted, border: `1px solid ${T.border}`, padding: '10px 16px', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT },
  del:      { background: 'transparent', color: T.red, border: 'none', padding: '8px 10px', fontSize: SZ, cursor: 'pointer', fontFamily: FONT },
  field:    { marginBottom: 18 },
  row2:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 18 },
  row3:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16, marginBottom: 18 },
  divider:  { borderTop: `1px solid ${T.border}`, margin: '28px 0' },
  head:     { fontSize: SZ, letterSpacing: '0.06em', color: T.text, textTransform: 'uppercase', marginBottom: 16, paddingBottom: 10, borderBottom: `1px solid ${T.border}` },
  catHead:  { fontSize: SZ, letterSpacing: '0.06em', color: T.text, textTransform: 'uppercase', fontWeight: 600, marginBottom: 10 },
};

// ── Field — outside component to prevent remount on every keystroke ───────────
function Field({ label, children, style }) {
  return (
    <div style={{ ...S.field, ...style }}>
      <label style={S.label}>{label}</label>
      {children}
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(dateStr, days) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${months[parseInt(m,10)-1]} ${parseInt(d,10)}, ${y}`;
}

function parseAmount(val) {
  return parseFloat(String(val).replace(/[$,]/g, '')) || 0;
}

function dollars(val) {
  const n = parseAmount(val);
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function calcDueDate(issueDate, terms, customDays) {
  if (!issueDate) return '';
  if (terms === 'Net-30') return addDays(issueDate, 30);
  if (terms === 'Net-60') return addDays(issueDate, 60);
  if (terms === 'Net-90') return addDays(issueDate, 90);
  if (terms === 'Custom') return addDays(issueDate, parseInt(customDays, 10) || 0);
  return '';
}

function makeInvoiceNum(issueDate, seq) {
  return `${(issueDate || today()).replace(/-/g, '')}-${seq || 1}`;
}

function newItem(desc = '', amount = '') {
  return { id: Math.random().toString(36).slice(2), desc, amount };
}

function subtotalNum(items) {
  return items.reduce((s, i) => s + parseAmount(i.amount), 0);
}

// ── Line items ────────────────────────────────────────────────────────────────

function LineItems({ items, onChange }) {
  const update = (id, field, val) => onChange(items.map(i => i.id === id ? { ...i, [field]: val } : i));
  const remove = id => onChange(items.filter(i => i.id !== id));
  const add    = ()  => onChange([...items, newItem()]);

  return (
    <div>
      {items.map(item => (
        <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 32px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <input style={S.input} placeholder="Description" value={item.desc} onChange={e => update(item.id, 'desc', e.target.value)} />
          <input style={{ ...S.input, textAlign: 'right' }} placeholder="0.00" value={item.amount} onChange={e => update(item.id, 'amount', e.target.value)} />
          <button onClick={() => remove(item.id)} style={S.del}>×</button>
        </div>
      ))}
      <button onClick={add} style={{ ...S.ghost, marginTop: 4 }}>+ Add Line</button>
    </div>
  );
}

// ── PDF generation ────────────────────────────────────────────────────────────

function generatePDF(form) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const PW  = 612;
  const ML  = 72, MR = 72;        // 1-inch margins (72pt = 1in)
  const CW  = PW - ML - MR;
  let y = 56;
  const mv  = n => { y += n; };
  const lh  = 12;
  const gap = 7;

  // Base table styles — Arial 10 (Helvetica is metrically identical to Arial in PDF)
  const tblStyles = {
    font: 'helvetica', fontSize: 10, cellPadding: 5,
    lineColor: [0, 0, 0], lineWidth: 0.5, textColor: [0, 0, 0],
  };

  // Banking / international tables — same row height as Payment Breakdown
  const bankStyles = { ...tblStyles, cellPadding: 5 };

  // Renders bold label + normal value on same line, separated by one space-char width
  const pair = (label, value, x) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, x, y);
    const labelW = doc.getTextWidth(label);
    doc.setFont('helvetica', 'normal');
    const spaceW = doc.getTextWidth(' ');
    if (value) doc.text(value, x + labelW + spaceW, y);
    mv(lh);
  };

  // Renders bold label on its own line, then normal value(s) below; skipped when value is empty
  const block = (label, value) => {
    if (!value?.trim()) return;
    mv(gap);
    doc.setFont('helvetica', 'bold');
    doc.text(label, ML, y);
    mv(lh);
    doc.setFont('helvetica', 'normal');
    doc.splitTextToSize(value, CW).forEach(l => { doc.text(l, ML, y); mv(lh); });
    mv(gap);
  };

  // didDrawCell hook: redraws the amount cell in subtotal rows with bold $ + normal number
  const boldDollarInSubtotal = (data) => {
    if (data.section !== 'body' || data.column.index !== 1) return;

    const rawRow = data.row.raw;
    if (!rawRow) return;
    const cell0   = rawRow[0];
    const label0  = typeof cell0 === 'object' ? cell0.content : cell0;
    if (label0 !== 'Subtotal') return; // only section subtotals, not SUBTOTAL TOTAL

    const rawCell = data.cell.raw;
    const content = typeof rawCell === 'object' ? rawCell.content : rawCell;
    if (!content || !content.toString().startsWith('$')) return;

    const amount  = content.toString();
    const numPart = amount.slice(1); // strip leading $

    doc.setFontSize(10);

    const pad    = data.cell.padding;
    const padR   = typeof pad === 'number' ? pad : (pad?.right ?? 5);
    const rightX = data.cell.x + data.cell.width - padR;
    const textY  = data.cell.y + data.cell.height / 2;

    // Cover existing text (inset 1pt to preserve border lines)
    doc.setFillColor(255, 255, 255);
    doc.rect(data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2, 'F');

    // Measure with respective weights
    doc.setFont('helvetica', 'bold');
    const dollarW = doc.getTextWidth('$');
    doc.setFont('helvetica', 'normal');
    const numW    = doc.getTextWidth(numPart);

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('$', rightX - numW - dollarW, textY, { baseline: 'middle' });
    doc.setFont('helvetica', 'normal');
    doc.text(numPart, rightX - numW, textY, { baseline: 'middle' });
  };

  const invoiceNum = makeInvoiceNum(form.issueDate, form.seq);
  const dueDate    = calcDueDate(form.issueDate, form.terms, form.customDays);
  const crewSub    = subtotalNum(form.crew);
  const iurSub     = subtotalNum(form.iur);
  const expSub     = subtotalNum(form.expenses);
  const grandSub   = crewSub + iurSub + expSub;
  const termsLabel = form.terms === 'Custom' ? `${form.customDays}-Day` : form.terms;

  // ── Page 1 ─────────────────────────────────────────────────────────────────

  // Header — Arial 16
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(`PHOTOGRAPHY INVOICE ${invoiceNum}`, PW / 2, y, { align: 'center' });
  mv(lh);

  // Client & my name — bold, immediately below header
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`${form.billToName || 'Client'}, Pierce Townsend`, PW / 2, y, { align: 'center' });
  mv(lh * 2);

  // Issue / Due — centered, bold labels
  const issueLabel = 'Issue Date:';
  const issueVal   = `  ${fmtDate(form.issueDate)}   `;
  const dueLabel   = 'Due Date:';
  const dueVal     = `  ${fmtDate(dueDate)}`;
  doc.setFont('helvetica', 'bold');
  const boldW = doc.getTextWidth(issueLabel + dueLabel);
  doc.setFont('helvetica', 'normal');
  const normW  = doc.getTextWidth(issueVal + dueVal);
  let cx = (PW - boldW - normW) / 2;
  doc.setFont('helvetica', 'bold');   doc.text(issueLabel, cx, y); cx += doc.getTextWidth(issueLabel);
  doc.setFont('helvetica', 'normal'); doc.text(issueVal,   cx, y); cx += doc.getTextWidth(issueVal);
  doc.setFont('helvetica', 'bold');   doc.text(dueLabel,   cx, y); cx += doc.getTextWidth(dueLabel);
  doc.setFont('helvetica', 'normal'); doc.text(dueVal,     cx, y);
  mv(lh * 2);

  // Bill To block
  pair('Bill To:', form.billToName || '', ML);
  doc.setFont('helvetica', 'bold');
  doc.text('Bill To Address:', ML, y);
  const addrLabelW = doc.getTextWidth('Bill To Address:');
  doc.setFont('helvetica', 'normal');
  if (form.billToAddress?.trim()) {
    const addrLines = doc.splitTextToSize(form.billToAddress, CW);
    // First line sits inline after the label
    doc.text(addrLines[0], ML + addrLabelW + doc.getTextWidth(' '), y);
    mv(lh);
    // Subsequent lines at left margin
    for (let i = 1; i < addrLines.length; i++) { doc.text(addrLines[i], ML, y); mv(lh); }
  } else {
    mv(lh);
  }

  mv(gap);
  if (form.contactName || form.contactEmail) {
    pair('Attn:', [form.contactName, form.contactEmail].filter(Boolean).join(', '), ML);
    mv(gap);
  }
  if (form.poNumber) {
    pair('PO #:', form.poNumber, ML);
  }

  mv(lh);

  pair('Job Name:', form.jobName || '', ML);
  pair('Dates:', form.jobDates || '', ML);
  pair('Location:', form.jobLocation || '', ML);

  block('Exploitation of Rights:', form.exploitation || '');

  // Payment Details paragraph
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Payment Details:', ML, y);
  mv(lh);

  const amt      = dollars(grandSub);
  const pre      = 'Total payment of ';
  const mid      = ' is subject to ';
  const post1    = ' payment terms. Payments made after thirty (30) calendar days of receiving this invoice are subject to a 3.5% interest charge per month.';
  const fullText = pre + amt + mid + termsLabel + post1;
  const lines    = doc.splitTextToSize(fullText, CW);
  if (lines.length > 0) {
    let lx = ML;
    doc.setFont('helvetica', 'normal'); doc.text(pre,        lx, y); lx += doc.getTextWidth(pre);
    doc.setFont('helvetica', 'bold');   doc.text(amt,        lx, y); lx += doc.getTextWidth(amt);
    doc.setFont('helvetica', 'normal'); doc.text(mid,        lx, y); lx += doc.getTextWidth(mid);
    doc.setFont('helvetica', 'bold');   doc.text(termsLabel, lx, y); lx += doc.getTextWidth(termsLabel);
    const rest1 = lines[0].slice((pre + amt + mid + termsLabel).length);
    doc.setFont('helvetica', 'normal');
    if (rest1) doc.text(rest1, lx, y);
    mv(lh);
    for (let i = 1; i < lines.length; i++) { doc.text(lines[i], ML, y); mv(lh); }
  }

  // Domestic banking table
  autoTable(doc, {
    startY: y,
    margin: { left: ML, right: MR },
    theme: 'grid',
    styles: bankStyles,
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 190 }, 1: { fontStyle: 'normal' } },
    body: [
      ['BENEFICIARY NAME',      'Pierce Bancroft Townsend'],
      ['BENEFICIARY ACCOUNT #', form.accountNum || ''],
      ['BENEFICIARY ROUTING #', form.routingNum || ''],
      ['BANK NAME',             'JPMorgan Chase Bank, N.A.'],
      ['BANK ADDRESS',          '919 Ranch Rd 620 S'],
      ['BANK CITY, STATE',      'Lakeway, Texas'],
      ['BANK ZIPCODE',          '78734'],
      ['ACCOUNT TYPE',          'Checking'],
    ],
  });
  y = doc.lastAutoTable.finalY + lh;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('For International Transfers:', ML, y);
  mv(gap);

  autoTable(doc, {
    startY: y,
    margin: { left: ML, right: MR },
    theme: 'grid',
    styles: bankStyles,
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 190 }, 1: { fontStyle: 'normal' } },
    body: [
      ['Invoice Currency', 'USD, United States Dollar, $'],
      ['SWIFT CODE',       'CHASUS33'],
      ['VAT Number',       'Not Applicable'],
    ],
  });

  // ── Page 2 ─────────────────────────────────────────────────────────────────

  doc.addPage();
  y = 60;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Payment Details (addtl.):', ML, y);
  mv(lh);
  doc.setFont('helvetica', 'normal');
  doc.splitTextToSize(form.paymentDetailsAddl || 'Not Applicable', CW).forEach(l => { doc.text(l, ML, y); mv(lh); });
  mv(gap);

  doc.setFont('helvetica', 'bold');
  doc.text('Payment Breakdown:', ML, y);
  mv(gap);

  const medGray = [175, 175, 175];

  autoTable(doc, {
    startY: y,
    margin: { left: ML, right: MR },
    theme: 'grid',
    styles: { ...tblStyles, fontStyle: 'normal' },
    columnStyles: { 1: { halign: 'right', cellWidth: 130 } },
    didDrawCell: boldDollarInSubtotal,
    body: [
      [{ content: `Crew (${form.crewLabel || '1 Photographer'})`, colSpan: 2, styles: { fillColor: medGray, fontStyle: 'bold', textColor: [0, 0, 0] } }],
      ...form.crew.map(i => [
        { content: i.desc || '', styles: { fontStyle: 'normal' } },
        { content: i.amount ? dollars(i.amount) : '', styles: { fontStyle: 'normal', halign: 'right' } },
      ]),
      [{ content: 'Subtotal', styles: { halign: 'right', fontStyle: 'bold' } }, { content: dollars(crewSub), styles: { fontStyle: 'normal', halign: 'right' } }],

      [{ content: 'Image Usage Rights', colSpan: 2, styles: { fillColor: medGray, fontStyle: 'bold', textColor: [0, 0, 0] } }],
      ...form.iur.map(i => [
        { content: i.desc || '', styles: { fontStyle: 'normal' } },
        { content: i.amount ? dollars(i.amount) : '', styles: { fontStyle: 'normal', halign: 'right' } },
      ]),
      [{ content: 'Subtotal', styles: { halign: 'right', fontStyle: 'bold' } }, { content: dollars(iurSub), styles: { fontStyle: 'normal', halign: 'right' } }],

      [{ content: 'Expenses', colSpan: 2, styles: { fillColor: medGray, fontStyle: 'bold', textColor: [0, 0, 0] } }],
      ...form.expenses.map(i => [
        { content: i.desc || '', styles: { fontStyle: 'normal' } },
        { content: i.amount ? dollars(i.amount) : '', styles: { fontStyle: 'normal', halign: 'right' } },
      ]),
      [{ content: 'Subtotal', styles: { halign: 'right', fontStyle: 'bold' } }, { content: dollars(expSub), styles: { fontStyle: 'normal', halign: 'right' } }],

      ['', ''],
      [{ content: 'SUBTOTAL TOTAL', styles: { halign: 'right', fontStyle: 'normal' } }, { content: dollars(grandSub), styles: { halign: 'right', fontStyle: 'normal' } }],
      [{ content: 'TOTAL USD', styles: { halign: 'right', fontStyle: 'bold' } }, { content: dollars(grandSub), styles: { halign: 'right', fontStyle: 'bold' } }],
      ['', ''],
    ],
  });
  y = doc.lastAutoTable.finalY + lh;

  if (form.receiptsRecords) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Receipts & Records:', ML, y);
    mv(lh);
    doc.setFont('helvetica', 'normal');
    doc.splitTextToSize(form.receiptsRecords, CW).forEach(l => { doc.text(l, ML, y); mv(lh); });
  }

  doc.save(`Invoice_${invoiceNum}.pdf`);
}

// ── Saved clients (localStorage) ─────────────────────────────────────────────

const CLIENTS_KEY = 'pbt_saved_invoice_clients';

function readSavedClients() {
  try { return JSON.parse(localStorage.getItem(CLIENTS_KEY)) || []; }
  catch { return []; }
}

function writeSavedClients(list) {
  localStorage.setItem(CLIENTS_KEY, JSON.stringify(list));
}

function SavedClientsBar({ clients, onLoad, onDelete }) {
  const [selected, setSelected] = useState('');

  function handleLoad() {
    const c = clients.find(x => x.id === selected);
    if (c) { onLoad(c); }
  }

  function handleDelete() {
    onDelete(selected);
    setSelected('');
  }

  if (clients.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
      <select style={{ ...S.input, flex: 1 }} value={selected} onChange={e => setSelected(e.target.value)}>
        <option value="">Load saved client...</option>
        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <button style={S.ghost} onClick={handleLoad} disabled={!selected}>Load</button>
      <button style={{ ...S.ghost, color: T.red, borderColor: T.red }} onClick={handleDelete} disabled={!selected}>Remove</button>
    </div>
  );
}

// ── InvoicePage ───────────────────────────────────────────────────────────────

export default function InvoicePage() {
  const [savedClients, setSavedClients] = useState(readSavedClients);

  function saveClient() {
    if (!form.billToName.trim()) return alert('Enter a Bill To name before saving.');
    const entry = {
      id: Date.now().toString(),
      name: form.billToName.trim(),
      billToName:    form.billToName,
      billToAddress: form.billToAddress,
      contactName:   form.contactName,
      contactEmail:  form.contactEmail,
      poNumber:      form.poNumber,
    };
    const updated = [entry, ...savedClients.filter(c => c.name !== entry.name)];
    writeSavedClients(updated);
    setSavedClients(updated);
  }

  function loadClient(c) {
    setForm(f => ({ ...f,
      billToName:    c.billToName    ?? f.billToName,
      billToAddress: c.billToAddress ?? f.billToAddress,
      contactName:   c.contactName   ?? f.contactName,
      contactEmail:  c.contactEmail  ?? f.contactEmail,
      poNumber:      c.poNumber      ?? f.poNumber,
    }));
  }

  function deleteClient(id) {
    const updated = savedClients.filter(c => c.id !== id);
    writeSavedClients(updated);
    setSavedClients(updated);
  }

  const [form, setForm] = useState({
    issueDate:          today(),
    seq:                '1',
    terms:              'Net-30',
    customDays:         '30',
    billToName:         '',
    billToAddress:      '',
    contactName:        '',
    contactEmail:       '',
    poNumber:           '',
    jobName:            '',
    jobDates:           '',
    jobLocation:        '',
    exploitation:       '',
    accountNum:         '',
    routingNum:         '',
    paymentDetailsAddl: '',
    receiptsRecords:    '',
    crewLabel:          '1 Photographer',
    crew:               [newItem('Creative Fees @ $', '')],
    iur:                [newItem('As described in Exploitation of Rights:', '')],
    expenses:           [newItem()],
  });

  const set = useCallback((key, val) => setForm(f => ({ ...f, [key]: val })), []);

  const invoiceNum = makeInvoiceNum(form.issueDate, form.seq);
  const dueDate    = calcDueDate(form.issueDate, form.terms, form.customDays);
  const crewSub    = subtotalNum(form.crew);
  const iurSub     = subtotalNum(form.iur);
  const expSub     = subtotalNum(form.expenses);
  const grandSub   = crewSub + iurSub + expSub;

  return (
    <div style={{ paddingBottom: 48, fontFamily: FONT }}>

      {/* Invoice # + Dates */}
      <div style={{ marginBottom: 28 }}>
        <div style={S.head}>Invoice</div>
        <div style={S.row3}>
          <Field label="Issue Date">
            <input type="date" style={S.input} value={form.issueDate} onChange={e => set('issueDate', e.target.value)} />
          </Field>
          <Field label="Payment Terms">
            <select style={S.input} value={form.terms} onChange={e => set('terms', e.target.value)}>
              <option>Net-30</option>
              <option>Net-60</option>
              <option>Net-90</option>
              <option>Custom</option>
            </select>
          </Field>
          {form.terms === 'Custom'
            ? <Field label="Days"><input style={S.input} value={form.customDays} onChange={e => set('customDays', e.target.value)} /></Field>
            : <Field label="Due Date"><input style={{ ...S.input, color: T.muted, background: '#eeece8' }} value={fmtDate(dueDate)} readOnly /></Field>
          }
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Field label="Sequence #" style={{ margin: 0, width: 100 }}>
            <input style={S.input} value={form.seq} onChange={e => set('seq', e.target.value)} />
          </Field>
          <span style={{ fontSize: SZ, color: T.muted, letterSpacing: '0.06em', marginTop: 20 }}>
            → Invoice #{invoiceNum}
          </span>
        </div>
      </div>

      <div style={S.divider} />

      {/* Client */}
      <div style={{ marginBottom: 28 }}>
        <div style={S.head}>Client</div>
        <SavedClientsBar clients={savedClients} onLoad={loadClient} onDelete={deleteClient} />
        <Field label="Bill To (Company / Client Name)">
          <input style={S.input} value={form.billToName} onChange={e => set('billToName', e.target.value)} />
        </Field>
        <Field label="Bill To Address">
          <textarea style={S.textarea} value={form.billToAddress} onChange={e => set('billToAddress', e.target.value)} />
        </Field>
        <div style={S.row2}>
          <Field label="Attn: (Contact Name)">
            <input style={S.input} value={form.contactName} onChange={e => set('contactName', e.target.value)} />
          </Field>
          <Field label="Contact Email">
            <input style={S.input} value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} />
          </Field>
        </div>
        <Field label="PO #">
          <input style={S.input} value={form.poNumber} onChange={e => set('poNumber', e.target.value)} />
        </Field>
        <div style={{ marginTop: 6 }}>
          <button style={S.ghost} onClick={saveClient}>Save Client Profile</button>
        </div>
      </div>

      <div style={S.divider} />

      {/* Job */}
      <div style={{ marginBottom: 28 }}>
        <div style={S.head}>Job</div>
        <Field label="Job Name">
          <input style={S.input} value={form.jobName} onChange={e => set('jobName', e.target.value)} />
        </Field>
        <div style={S.row2}>
          <Field label="Dates">
            <input style={S.input} value={form.jobDates} onChange={e => set('jobDates', e.target.value)} placeholder="e.g. January 10, 2026" />
          </Field>
          <Field label="Location">
            <input style={S.input} value={form.jobLocation} onChange={e => set('jobLocation', e.target.value)} />
          </Field>
        </div>
        <Field label="Exploitation of Rights">
          <textarea style={S.textarea} value={form.exploitation} onChange={e => set('exploitation', e.target.value)} />
        </Field>
      </div>

      <div style={S.divider} />

      {/* Banking */}
      <div style={{ marginBottom: 28 }}>
        <div style={S.head}>Banking</div>
        <div style={S.row2}>
          <Field label="Account #">
            <input style={S.input} value={form.accountNum} onChange={e => set('accountNum', e.target.value)} autoComplete="off" />
          </Field>
          <Field label="Routing #">
            <input style={S.input} value={form.routingNum} onChange={e => set('routingNum', e.target.value)} autoComplete="off" />
          </Field>
        </div>
        <Field label="Payment Details (Addtl.)">
          <textarea style={S.textarea} value={form.paymentDetailsAddl} onChange={e => set('paymentDetailsAddl', e.target.value)} placeholder="Not Applicable" />
        </Field>
      </div>

      <div style={S.divider} />

      {/* Payment Breakdown */}
      <div style={{ marginBottom: 28 }}>
        <div style={S.head}>Payment Breakdown</div>

        {/* Crew */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <span style={S.catHead}>Crew</span>
            <input style={{ ...S.input, width: 200, fontSize: 12 }} placeholder="1 Photographer" value={form.crewLabel} onChange={e => set('crewLabel', e.target.value)} />
          </div>
          <LineItems items={form.crew} onChange={v => set('crew', v)} />
          <div style={{ textAlign: 'right', fontSize: SZ, color: T.muted, letterSpacing: '0.06em', marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
            Subtotal: {dollars(crewSub)}
          </div>
        </div>

        {/* Image Usage Rights */}
        <div style={{ marginBottom: 24 }}>
          <div style={S.catHead}>Image Usage Rights</div>
          <LineItems items={form.iur} onChange={v => set('iur', v)} />
          <div style={{ textAlign: 'right', fontSize: SZ, color: T.muted, letterSpacing: '0.06em', marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
            Subtotal: {dollars(iurSub)}
          </div>
        </div>

        {/* Expenses */}
        <div style={{ marginBottom: 24 }}>
          <div style={S.catHead}>Expenses</div>
          <LineItems items={form.expenses} onChange={v => set('expenses', v)} />
          <div style={{ textAlign: 'right', fontSize: SZ, color: T.muted, letterSpacing: '0.06em', marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
            Subtotal: {dollars(expSub)}
          </div>
        </div>

        {/* Totals */}
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: SZ, color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Subtotal Total</span>
            <span style={{ fontSize: SZ, color: T.muted }}>{dollars(grandSub)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
            <span style={{ fontSize: SZ, color: T.text, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Total USD</span>
            <span style={{ fontSize: 15, color: T.text }}>{dollars(grandSub)}</span>
          </div>
        </div>

        {/* Receipts & Records */}
        <Field label="Receipts & Records">
          <textarea style={{ ...S.textarea, minHeight: 96 }} value={form.receiptsRecords} onChange={e => set('receiptsRecords', e.target.value)} />
        </Field>
      </div>

      <div style={S.divider} />

      <button style={S.btn} onClick={() => generatePDF(form)}>
        Download PDF
      </button>
    </div>
  );
}
